import * as React from 'react';

import { cn } from '@/lib/utils';
import { useCallback } from 'react';

type InputProps = {
  suffix?: string;
  prefix?: string;
  onValueChange?: (value: string | number | boolean) => void;
} & React.ComponentProps<'input'>;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (allProps, ref) => {
    const {
      className,
      type = 'text',
      suffix,
      prefix,
      onChange,
      ...props
    } = allProps;
    const isNumber = type === 'number';
    // Custom onChange handler to convert value for number/boolean types
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        let convertedValue: string | number | boolean | null = e.target.value;
        if (isNumber) {
          convertedValue =
            e.target.value === '' ? null : Number(e.target.value);
        }
        if (onChange) {
          // Create a synthetic event with the converted value
          const eventWithConvertedValue = {
            ...e,
            target: {
              ...e.target,
              value: convertedValue,
            },
          };
          onChange(
            eventWithConvertedValue as React.ChangeEvent<HTMLInputElement>,
          );
        }
      },
      [isNumber, onChange],
    );
    return (
      <div className="flex">
        {prefix && (
          <span className="flex items-center justify-center rounded-l-md bg-neutral-100 px-2 text-sm text-neutral-500 peer-focus:bg-neutral-200">
            {prefix}
          </span>
        )}
        <input
          type={type}
          className={cn(
            'invalid:border-3 border-1 flex h-9 w-full rounded-md border-neutral-200 bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-neutral-950 placeholder:text-neutral-500 invalid:border-solid invalid:border-red-600 invalid:bg-red-50 invalid:text-red-700 focus:border-primary-500 invalid:focus:!border-red-600 disabled:cursor-not-allowed disabled:opacity-50',
            prefix ? 'rounded-l-none pl-2' : '',
            suffix ? 'rounded-r-none pr-8' : '',
            className,
          )}
          ref={ref}
          onChange={handleChange}
          {...props}
        />
        {suffix && (
          <span className="flex items-center justify-center rounded-r-md bg-neutral-100 px-2 text-sm text-neutral-500 peer-focus:bg-neutral-200">
            {suffix}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
